import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { formatMaterialName, Material } from 'src/api/endpoints/materials';
import { getMaterials } from 'src/store/student/material/material.actions';
import { DownloadMaterialFilesButton } from 'src/pages/Materials';
import useDateFormatter from 'src/hooks/useDateFormatter';
import useErrorHandler from 'src/hooks/useErrorHandler';
import React, { FC, useEffect, useState } from 'react';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { Practice } from 'src/api/endpoints/practice';
import useAppSelector from 'src/hooks/useAppSelector';
import { InputText } from 'primereact/inputtext';
import { IFilter } from './MaterialPage.types';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import Loader from 'src/components/Loader';
import './MaterialPage.scss';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';

export const StudentMaterialsPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
) => {
    // state
    const [materials, setMaterials] = useState<Material[]>([]);

    const [filter, setFilter] = useState<IFilter>({
        subjectId: 'all',
    });

    // redux
    const { hasLoaded, isLoading, data } = useAppSelector(
        state => state.materialReducer
    );

    const dispatch = useAppDispatch();
    // hooks
    const _ = useDateFormatter();
    const errorHandler = useErrorHandler();

    useEffect(() => {
        document.title = 'Методические материалы';

        if (!hasLoaded) {
            dispatch(getMaterials())
                .unwrap()
                .catch(error => errorHandler(error));
        }
    }, []);

    useEffect(() => {
        setMaterials(data.materials);
    }, [data.materials]);

    useEffect(() => {
        if (areMaterials(filter, data.materials)) {
            return setMaterials(data.materials);
        }

        let qs: Material[] = data.materials;

        if (filter.subjectId && filter.subjectId !== 'all') {
            qs = qs.filter(
                (m: Material) => '' + m.subject.id === filter.subjectId
            );
        }

        if (filter.search && filter.search.trim().length) {
            const search: string = filter.search;

            qs = qs.filter((material: Material) =>
                formatMaterialName(material)
                    .toLocaleLowerCase()
                    .includes(search.toLocaleLowerCase().trim())
            );
        }

        setMaterials(qs);
    }, [filter]);

    return (
        <StudentLayout pageName="materials-page" auth={props}>
            <div className="card mt-24">
                <div className="header flex align-items-center justify-content-between materials-bg">
                    <div className="title big">{'Методические материалы'}</div>
                </div>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className="content">
                        <div className="flex filter-container filter-container">
                            <Dropdown
                                className={'w-300'}
                                filter={true}
                                options={[
                                    {
                                        label: 'Все предметы',
                                        value: 'all',
                                    },
                                    ...data.subjects.map(s => ({
                                        label: s.name,
                                        value: '' + s.id,
                                    })),
                                ]}
                                value={filter.subjectId}
                                onChange={event => {
                                    setFilter(prev => ({
                                        ...prev,
                                        subjectId: event.value,
                                    }));
                                }}
                                placeholder={'Выберите предмет'}
                            />
                            <span className="p-input-icon-right ml-24 mt">
                                <i className="pi pi-search" />
                                <InputText
                                    value={filter.search}
                                    onChange={event =>
                                        setFilter(prev => ({
                                            ...prev,
                                            search: event.target.value,
                                        }))
                                    }
                                    placeholder="Поиск"
                                />
                            </span>
                        </div>
                        <div className="grid mt-24">
                            {materials.length ? (
                                materials.map(material => (
                                    <div
                                        className="lg:col-4 md:col-6 col-12"
                                        key={material.id}
                                        id={`m${material.id}`}
                                    >
                                        <div className="area material bg flex flex-column justify-content-between h-full">
                                            <div className="body">
                                                <div className="flex justify-content-between preview-container">
                                                    <div
                                                        className="source-label"
                                                        style={{
                                                            color: '#3B82F6',
                                                            backgroundColor:
                                                                '#3B82F620',
                                                        }}
                                                    >
                                                        {'У'}
                                                    </div>
                                                    <small className="date block ml-72">
                                                        {_(material.createdAt)}
                                                    </small>
                                                </div>
                                                <div
                                                    className="text mt-24"
                                                    style={{ fontSize: 18 }}
                                                >
                                                    {material.subject.name}
                                                </div>
                                                <div className="text bold mt-24">
                                                    {formatMaterialName(
                                                        material
                                                    )}
                                                </div>
                                            </div>
                                            <div className="footer mt-24">
                                                {material.sourceLink && (
                                                    <Button
                                                        className={
                                                            'p-button-sm flex-grow-1'
                                                        }
                                                        loading={isLoading}
                                                        icon={
                                                            'pi pi-external-link'
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                !material.sourceLink
                                                            )
                                                                return;

                                                            window
                                                                .open(
                                                                    material.sourceLink,
                                                                    '_blank'
                                                                )
                                                                ?.focus();
                                                        }}
                                                        label={
                                                            'Внешний источник'
                                                        }
                                                    />
                                                )}

                                                {material.meta.hasLesson && (
                                                    <DownloadMaterialFilesButton
                                                        material={material}
                                                        segment={'lesson'}
                                                        label={'Скачать урок'}
                                                    />
                                                )}

                                                {material.meta.hasPractice && (
                                                    <DownloadMaterialFilesButton
                                                        material={material}
                                                        segment={'practice'}
                                                        label={
                                                            'Скачать практическую'
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="fz-20 px-2">
                                    Здесь пока ничего нет
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </StudentLayout>
    );
};

const areMaterials = (
    filter: IFilter,
    qs: Material[] | Practice[]
): qs is Material[] => {
    return !filter.search && !filter.subjectId;
};
